import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'

interface ServiceCheck {
	name: string
	url: string
}

interface ServiceFailure {
	name: string
	url: string
	reason: string
}

/**
 * Bestimmt die Backend-URL für den Healthcheck-Probe.
 *
 * Prod-Topologie: Landingpage auf Hauptdomain (verein-checkin.de), Backend pro Mandant
 * als Subdomain (z. B. sparkasse-oberhessen.verein-checkin.de). Lokal unterscheiden
 * sich Landing und Backend nur am Port.
 *
 * - Lokal: explizit gesetzter `apiBaseUrl`.
 * - Web (Browser): `window.location.origin` matcht immer den aktuellen Mandanten-Host.
 * - Mobile (Capacitor): vor der Tenant-Auswahl unbekannt → erster Tenant aus der Liste
 *   als Vertreter, damit zumindest ein Backend-Host validiert wird.
 */
function resolveBackendBaseUrl(
	publicConfig: { apiBaseUrl?: string },
	isLocal: boolean,
	tenantBaseUrls: string[],
): string | undefined {
	if (isLocal && publicConfig.apiBaseUrl) return publicConfig.apiBaseUrl
	if (!Capacitor.isNativePlatform()) return window.location.origin
	return tenantBaseUrls[0]
}

export default defineNuxtPlugin(async () => {
	const publicConfig = useRuntimeConfig().public
	const { isLocal } = useEnvironment()
	const { fetchTenants, tenants } = useTenant()

	const failures: ServiceFailure[] = []

	// Landingpage hostet die Mandantenliste. Fetch hier ausführen, damit das Tenant-Plugin
	// (03) auf bereits geladene Daten zurückgreift und ein Ausfall hier zentral diagnostiziert
	// wird statt das Tenant-Plugin unbehandelt crashen zu lassen.
	const tenantsUrl = (publicConfig.tenant as { tenantsUrl?: string }).tenantsUrl
	try {
		await fetchTenants()
	} catch (err) {
		failures.push({
			name: 'Landingpage',
			url: tenantsUrl ? `${tenantsUrl}/api/tenants` : '(tenantsUrl nicht konfiguriert)',
			reason: err instanceof Error ? err.message : String(err),
		})
	}

	const services: ServiceCheck[] = [
		{ name: 'Keycloak', url: `${publicConfig.keycloak.url}/realms/master` },
	]

	const tenantBaseUrls = tenants.value.map(t => t.baseUrl)
	const backendBaseUrl = resolveBackendBaseUrl(publicConfig, isLocal, tenantBaseUrls)
	if (backendBaseUrl) {
		services.push({ name: 'Backend', url: `${backendBaseUrl}/api/v1/health` })
	} else {
		failures.push({
			name: 'Backend',
			url: '(keine Backend-URL ermittelbar)',
			reason: 'Mandantenliste leer und kein apiBaseUrl gesetzt',
		})
	}

	const probeResults = await Promise.allSettled(
		services.map(async ({ name, url }) => {
			try {
				await $fetch(url, { ignoreResponseError: false, timeout: 5000 })
				return { name, url }
			} catch (err) {
				const reason = err instanceof Error ? err.message : String(err)
				throw { name, url, reason } satisfies ServiceFailure
			}
		}),
	)

	failures.push(
		...probeResults
			.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
			.map(r => r.reason as ServiceFailure),
	)

	if (failures.length > 0) {
		await SplashScreen.hide()

		throw createError({
			statusCode: 503,
			statusMessage: `Healthcheck fehlgeschlagen – nicht erreichbar: ${failures.map(f => f.name).join(', ')}`,
			data: { failures },
			fatal: true,
		})
	}
})
